import USER_STATUS from "../Routes/RecoverUserStatus";
import { HTTP_SUCCESS_STATUS, ResponseStatus } from "../api/core/ResponseStatus";
import {
  customerProjectsEnabled,
  enterNativeMode,
  nativeGatewayBaseUrl,
  storeOrganicToken,
} from "./nativeMode";

/**
 * 409 `LEAD_SERVED_NATIVE` FUERA del login (F2 de la cartera IT, 5-nov-2026).
 *
 * Con `IT_BOOK_PORTAL_NATIVE=true` el monolito deja de servir el portal legacy a una
 * póliza de la cartera IT con la lectura native encendida: CUALQUIER ruta de la app
 * Customer responde
 * `409 {"error":"LEAD_SERVED_NATIVE","reason":"IT_BOOK_SERVED_NATIVE","nativePortalUrl":…}`,
 * con `nativeLoginToken` (JWT de lead de 15 min) si la sesión es la de ese lead. Una
 * sesión legacy abierta antes del `switch-reads` (el JWT del monolito vive 24 h) veía
 * errores en todas las pantallas. Aquí se sale del portal legacy sin que el cliente
 * tenga que hacer nada:
 *
 * - con `nativeLoginToken`: se canjea en el gateway (`POST /auth/lead/login`), igual que
 *   el login (`Lead.service.ts`, `redeemNativeLink`), se guarda la sesión native como la
 *   guarda la pantalla de login (`token`, `lead`, `userStatus` de `useCache`) y se recarga
 *   en el Dashboard native;
 * - sin token (ruta pública por ULID, o sesión de otro lead): se lleva al cliente a
 *   `nativePortalUrl` (la entrada del Portal Cliente native: Google o código por email);
 * - sin ninguno de los dos (p. ej. el freno de escrituras de un alta migrada, que solo
 *   manda `error`), o si el canje falla: el error de siempre.
 *
 * El login (`/lead/login`) se queda fuera: ya canjea el token él mismo y devuelve la
 * sesión a su pantalla.
 */
export const LEAD_SERVED_NATIVE = "LEAD_SERVED_NATIVE";

/** Claves de `useCache` de GetLifeContext (localStorage) que escribe la pantalla de login. */
const SESSION_KEYS = { token: "token", lead: "lead", status: "userStatus" } as const;

const LOGIN_PATH = /\/lead\/login\/?(\?|$)/;

export interface ServedNativeConflict {
  nativeLoginToken?: string;
  nativePortalUrl?: string;
}

/** Lo que hace falta del navegador; inyectable para los tests. */
export interface BrowserExit {
  assign(url: string): void;
  currentHref(): string;
}

const browser: BrowserExit = {
  assign: (url) => window.location.assign(url),
  currentHref: () => window.location.href,
};

/** ¿Es el 409 del portal native, y no la llamada de login (que lo resuelve ella)? */
export function isServedNativeConflict(status: number, message: unknown, path: unknown): boolean {
  return (
    ResponseStatus.CONFLICT === status &&
    LEAD_SERVED_NATIVE === message &&
    !("string" === typeof path && LOGIN_PATH.test(path))
  );
}

/**
 * Sale del portal legacy. `true` si ya está navegando (la llamada original no debe
 * resolverse: la página se va), `false` si no se ha podido (sigue el error de siempre).
 */
export async function leaveLegacySession(
  conflict: ServedNativeConflict,
  exit: BrowserExit = browser
): Promise<boolean> {
  const token = conflict.nativeLoginToken;
  if ("string" === typeof token && "" !== token && "" !== nativeGatewayBaseUrl()) {
    const target = await redeem(token);
    if (null !== target) {
      exit.assign(target);
      return true;
    }
  }
  const portal = conflict.nativePortalUrl;
  if ("string" === typeof portal && "" !== portal && !exit.currentHref().startsWith(portal)) {
    exit.assign(portal);
    return true;
  }

  return false;
}

/** Canjea el enlace en el gateway y deja la sesión native; devuelve adónde ir, o null si falla. */
async function redeem(link: string): Promise<string | null> {
  try {
    const response = await fetch(`${nativeGatewayBaseUrl()}/auth/lead/login`, {
      method: "POST",
      mode: "cors",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ otp: link }),
    });
    if (!HTTP_SUCCESS_STATUS.includes(response.status)) {
      return null;
    }
    const session = await response.json();
    if ("string" !== typeof session?.token || "" === session.token) {
      return null;
    }
    storeOrganicToken(session.token);
    enterNativeMode();
    const status = "string" === typeof session.status ? session.status : "";
    storeSession(session.token, "string" === typeof session.id ? session.id : "", status);

    return landing(status);
  } catch {
    return null;
  }
}

function storeSession(token: string, leadId: string, status: string): void {
  try {
    window.localStorage.setItem(SESSION_KEYS.token, token);
    window.localStorage.setItem(SESSION_KEYS.lead, leadId);
    window.localStorage.setItem(SESSION_KEYS.status, status);
  } catch {
    /* sin localStorage: la sesión native de esta pestaña sigue en sessionStorage */
  }
}

/** Donde aterriza el login native (`LoginOtpPage`): el Dashboard, o la pantalla de su estado. */
function landing(status: string): string {
  if (customerProjectsEnabled()) {
    return "/dashboard/global";
  }
  const routes = (USER_STATUS as Record<string, string[] | undefined>)[status];

  return routes?.[0] ?? "/dashboard/global";
}
